import { AUTH_SUCCESS_MESSAGES } from '@parlay-picker/shared/authMessages';
import type { ReactElement } from 'react';

interface PauseData {
  email: string;
  oauthProvider?: string;
  oauthIdToken?: string;
}

interface AccountPauseModalProps {
  pauseData: PauseData;
  password: string;
  rememberMe: boolean;
  resumeLoading: boolean;
  resumeError: string;
  onResumeLoadingChange: (value: boolean) => void;
  onResumeErrorChange: (value: string) => void;
  onPauseDataClear: () => void;
  onNavigateHome: () => void;
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<unknown>;
  onShowToast: (message: string, type: 'success' | 'error') => void;
}

export function AccountPauseModal({
  pauseData,
  password,
  rememberMe,
  resumeLoading,
  resumeError,
  onResumeLoadingChange,
  onResumeErrorChange,
  onPauseDataClear,
  onNavigateHome,
  onLogin,
  onShowToast,
}: AccountPauseModalProps): ReactElement {
  const handleResume = async (): Promise<void> => {
    onResumeLoadingChange(true);
    onResumeErrorChange('');
    try {
      const body: Record<string, string> = { email: pauseData.email };
      if (pauseData.oauthProvider && pauseData.oauthIdToken) {
        body.provider = pauseData.oauthProvider;
        body.idToken = pauseData.oauthIdToken;
      } else {
        body.password = password;
      }

      const res = await fetch('/api/auth/account/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || 'Resume failed');
      }

      onPauseDataClear();
      onShowToast(AUTH_SUCCESS_MESSAGES.resumed.toast, 'success');
      if (pauseData.oauthProvider) {
        window.location.reload();
      } else {
        await onLogin(pauseData.email, password, rememberMe);
        onNavigateHome();
      }
    } catch (err) {
      onResumeErrorChange((err as Error).message || 'Resume failed');
    } finally {
      onResumeLoadingChange(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-theme-surface rounded-2xl p-6 border border-theme-border w-full max-w-sm shadow-xl">
        <h3 className="text-lg font-semibold text-amber-500 mb-2">Account Paused</h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          Your account ({pauseData.email}) is currently paused. Would you like to resume?
        </p>

        {resumeError && <p className="text-sm text-red-400 mb-3">{resumeError}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            disabled={resumeLoading}
            onClick={handleResume}
            className="flex-1 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {resumeLoading ? 'Resuming...' : 'Resume Account'}
          </button>
          <button
            type="button"
            onClick={() => {
              onPauseDataClear();
              onResumeErrorChange('');
            }}
            className="px-4 py-2 bg-theme-bg hover:bg-theme-border text-theme-text-secondary font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
